import type { RelationshipDirectionSet, RelationshipPair } from '../../entities/graph-document';

export interface ExplicitRelationshipPairInput {
  sourceNodeId: string;
  targetNodeId: string;
  sourceToTargetLabels: string[];
  targetToSourceLabels: string[];
  createdAt?: string;
  updatedAt?: string;
  createdAtTimeZone?: string;
  updatedAtTimeZone?: string;
}

export function canonicalRelationshipPairId(firstNodeId: string, secondNodeId: string): string {
  const [sourceNodeId, targetNodeId] = [firstNodeId, secondNodeId].sort((left, right) => left.localeCompare(right));
  return `${sourceNodeId}::${targetNodeId}`;
}

export function normalizeRelationshipLabels(labels: string[]): string[] {
  return labels.map((label) => label.trim()).filter((label) => label.length > 0);
}

export function normalizeExplicitRelationshipPair(input: ExplicitRelationshipPairInput): RelationshipPair {
  const now = new Date().toISOString();
  const [firstNodeId, secondNodeId] = [input.sourceNodeId, input.targetNodeId].sort((left, right) => left.localeCompare(right)) as [string, string];
  const sourceMatchesCanonicalOrder = input.sourceNodeId === firstNodeId;
  const firstToSecondLabels = normalizeRelationshipLabels(
    sourceMatchesCanonicalOrder ? input.sourceToTargetLabels : input.targetToSourceLabels
  );
  const secondToFirstLabels = normalizeRelationshipLabels(
    sourceMatchesCanonicalOrder ? input.targetToSourceLabels : input.sourceToTargetLabels
  );

  return {
    pairId: canonicalRelationshipPairId(input.sourceNodeId, input.targetNodeId),
    endpoints: [firstNodeId, secondNodeId],
    directions: [
      createDirectionSet(firstNodeId, secondNodeId, firstToSecondLabels),
      createDirectionSet(secondNodeId, firstNodeId, secondToFirstLabels),
    ],
    createdAt: input.createdAt ?? now,
    updatedAt: input.updatedAt ?? input.createdAt ?? now,
    createdAtTimeZone: input.createdAtTimeZone,
    updatedAtTimeZone: input.updatedAtTimeZone,
  };
}

export function removeNodeFromRelationships(relationships: RelationshipPair[], nodeId: string): RelationshipPair[] {
  return relationships.filter((pair) => !pair.endpoints.includes(nodeId));
}

function createDirectionSet(sourceNodeId: string, targetNodeId: string, labels: string[]): RelationshipDirectionSet {
  return {
    sourceNodeId,
    targetNodeId,
    labels,
  };
}